import type { ProtocolStep } from "../injection-protocol/types";

/**
 * One tap per step, including the injection.
 *
 * The session reducer requires an explicit confirmation event before any step
 * marked `requiresConfirmation` can be passed, and that contract is unchanged —
 * `ovidrel-protocol.test.ts` still refuses to let the injection be passed
 * without one. What changed is how many buttons it takes to produce that event.
 *
 * Every step used to ask twice: "I'm ready", then "done, next step". For a check
 * — read the timing, inspect the solution — those are the same question, and
 * asking it twice adds a tap to every step of a procedure she is performing with
 * a needle in her hand. Friction spent where it buys nothing is friction she
 * learns to tap through, which is how a deliberate gate quietly stops being
 * deliberate.
 *
 * The injection was kept separate at first, on the reading that #2267's second
 * safety principle — the patient confirms readiness before an irreversible step
 * — required its own button. It now advances in one tap too, at Anaïs's explicit
 * direction on 2026-09-15, and the reasoning is worth recording because a
 * reviewer will ask:
 *
 * - The principle guards against a *system* acting. Here nothing is actuated:
 *   the app displays approved text and she holds the syringe. There is no dose
 *   to trigger silently and no device to fire.
 * - The confirmation event still happens. One tap sends `confirm` then
 *   `advance`, in that order, so the reducer sees exactly what it saw before and
 *   the step still cannot be skipped.
 * - What is genuinely lost is a deliberate pause at the one moment that cannot
 *   be undone. That is a clinical-review question rather than an engineering
 *   one, and it has not been reviewed yet — it belongs on the list that goes to
 *   clinical sign-off before any of this reaches a patient.
 */

/**
 * Whether the step's single action must also carry the confirmation.
 *
 * True whenever the step requires one and has not had it yet. The caller sends
 * both events on that tap; otherwise it sends only `advance`.
 */
export function actionCarriesConfirmation(
  step: ProtocolStep,
  confirmedCurrentStep: boolean,
): boolean {
  return step.requiresConfirmation && !confirmedCurrentStep;
}

/**
 * The i18n leaf for a step's single action.
 *
 * "Done, next step" is wrong on a step where nothing was done. The Ovidrel walk
 * opens on the timing rule — inject at the exact hour, retrieval is 34 to 36
 * hours later, set several alarms — which she has read, not performed. Being
 * asked to say "it's done" about a thing she has only taken in is the kind of
 * small wrongness that makes a procedure feel written for someone else.
 *
 * So checks confirm and everything else reports. "All good" carries both senses a
 * `verify` step needs: taking in the timing rule, and confirming an observation —
 * step 3 asks her to look at the solution and check the dose, and "noted" would
 * be wrong there in the other direction. `prepare` and `aftercare` are real
 * actions — wash your hands, clean the site, apply pressure — and the injection
 * most of all.
 */
export function advanceLabelKey(step: ProtocolStep): string {
  return step.kind === "verify" ? "all_good_next_step" : "done_next_step";
}
